import {mapObject} from '@benchling/js-utils';

import {pluralize} from 'BenchlingUtil';
import * as EventConstants from 'constants/EventConstants';
import FileStates from 'FileStates';
import AppDispatcher from 'lib/Dispatcher';
import {AutoFillFeatureResultProps} from 'molbio/SequenceAnalyticsHelper';
import {filesFetchCompleted} from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import * as AutoFillFieldDAO from 'registry/AutoFillFieldDAO';
import {AutoFillFeatureResponse, AutoFillPartsResponse} from 'registry/AutoFillFieldDAO';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';

async function updateSequences(
  response: AutoFillPartsResponse | AutoFillFeatureResponse,
  sequenceIds: Array<string>
): Promise<void> {
  // We need to submit FILE_TAGS_UPDATED to update the metadata tab. In the future, the metadata tab should
  // use the regular store/cache to access tag values, which would make this line unnecessary.
  AppDispatcher.dispatch({
    actionType: EventConstants.FILE_TAGS_UPDATED,
    tagData: Object.entries(response).map(([sequenceId, sequenceResponse]) => ({
      id: sequenceId,
      tags: sequenceResponse.sequenceSummary.tags,
    })),
  });
  getStore().dispatch(filesFetchCompleted(mapObject(response, (r) => r.sequenceSummary)));
  await FileStates.get().updateSequenceVersions(sequenceIds, {showHistoryPanel: false});
}

type AutoFillFeatureName = 'part' | 'transcription' | 'translation';

function getAutoFillActionFromName(
  featureName: AutoFillFeatureName
): (sequenceIds: Array<string>) => Promise<AutoFillPartsResponse | AutoFillFeatureResponse> {
  const autoFillFeatureNameToAction: {
    [featureName in AutoFillFeatureName]: (
      sequenceIds: Array<string>
    ) => Promise<AutoFillPartsResponse | AutoFillFeatureResponse>;
  } = {
    part: AutoFillFieldDAO.autofillParts,
    transcription: AutoFillFieldDAO.autofillTranscriptions,
    translation: AutoFillFieldDAO.autofillTranslations,
  };
  return autoFillFeatureNameToAction[featureName];
}

interface AutoFillProps {
  autoFillFeatureName: AutoFillFeatureName;
  customTaskHeading?: string;
  customSuccessMessage?: string;
  analyticsFn?: (analyticsProps: AutoFillFeatureResultProps) => void;
}

interface AutoFillBioentityLinks extends AutoFillProps {
  sequenceId: string;
}

async function autoFillBioentityLinks({
  sequenceId,
  autoFillFeatureName,
  customTaskHeading,
  customSuccessMessage,
  analyticsFn,
}: AutoFillBioentityLinks): Promise<void> {
  const pluralFeatureName = pluralize(2, autoFillFeatureName);
  const taskId = TrayTaskActions.createTrayTask({
    heading: customTaskHeading || `Auto-filling ${pluralFeatureName}`,
    progress: TrayTaskConstants.INDETERMINATE_PROGRESS,
  });
  try {
    const response = await getAutoFillActionFromName(autoFillFeatureName)([sequenceId]);
    await updateSequences(response, [sequenceId]);
    let message;
    if (response[sequenceId].didModifySequence) {
      message = customSuccessMessage || `Successfully ran auto-fill ${pluralFeatureName}.`;
    } else {
      message = `No new ${pluralFeatureName} were found.`;
    }
    const {warningMessages} = response[sequenceId];
    if (warningMessages && warningMessages.length > 0) {
      message += `\n\n**Warnings:**\n\n${warningMessages.join('\n\n')}`;
    }
    const autofillCount = response[sequenceId].autofillCount;
    analyticsFn?.({sequenceId, autofillCount, autofillErrors: warningMessages});
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.SUCCESS_PROGRESS,
      heading: message,
    });
  } catch (e) {
    // Our top-level handler will show an appropriate error message, so just hide the current tray task.
    TrayTaskActions.removeTrayTask(taskId);
    throw e;
  }
}

interface BulkAutoFillBioentityLinks extends AutoFillProps {
  sequenceIds: Array<string>;
}

async function bulkAutoFillBioentityLinks({
  sequenceIds,
  autoFillFeatureName,
  customTaskHeading,
  customSuccessMessage,
  analyticsFn,
}: BulkAutoFillBioentityLinks): Promise<void> {
  const pluralFeatureName = pluralize(2, autoFillFeatureName);
  const length = sequenceIds.length;
  const taskId = TrayTaskActions.createTrayTask({
    heading:
      customTaskHeading ||
      `Auto-filling ${pluralFeatureName} on ${length} ${pluralize(length, 'DNA sequence')}`,
    progress: TrayTaskConstants.INDETERMINATE_PROGRESS,
  });
  try {
    const response = await getAutoFillActionFromName(autoFillFeatureName)(sequenceIds);
    await updateSequences(response, sequenceIds);
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.SUCCESS_PROGRESS,
      heading:
        customSuccessMessage ||
        `Successfully ran auto-fill ${pluralFeatureName} on ${length} ${pluralize(length, 'sequence')}.`,
    });
    for (const [sequenceId, autofillResponse] of Object.entries(response)) {
      analyticsFn?.({
        sequenceId,
        autofillCount: autofillResponse.autofillCount,
        autofillErrors: autofillResponse.warningMessages,
      });
    }
  } catch (e) {
    // Our top-level handler will show an appropriate error message, so just hide the current tray task.
    TrayTaskActions.removeTrayTask(taskId);
    throw e;
  }
}

export async function autoFillPartFields(sequenceId: string): Promise<void> {
  await autoFillBioentityLinks({
    sequenceId,
    autoFillFeatureName: 'part',
    customTaskHeading: 'Auto-filling part fields',
    customSuccessMessage: 'Successfully replaced sequence with parts.',
  });
}

export async function bulkAutoFillPartFields(sequenceIds: Array<string>): Promise<void> {
  const length = sequenceIds.length;
  const customTaskHeading = `Auto-filling part fields on ${length} ${pluralize(length, 'DNA sequence')}`;
  const customSuccessMessage = `Successfully replaced ${length} ${pluralize(length, 'sequence')} with parts.`;
  await bulkAutoFillBioentityLinks({
    sequenceIds,
    autoFillFeatureName: 'part',
    customTaskHeading,
    customSuccessMessage,
  });
}

export async function autofillTranslations(sequenceId: string): Promise<void> {
  await autoFillBioentityLinks({
    sequenceId,
    autoFillFeatureName: 'translation',
  });
}

export async function bulkAutofillTranslations(sequenceIds: Array<string>): Promise<void> {
  await bulkAutoFillBioentityLinks({sequenceIds, autoFillFeatureName: 'translation'});
}

export async function autofillTranscriptions(
  sequenceId: string,
  analyticsFn: (props: AutoFillFeatureResultProps) => void
): Promise<void> {
  await autoFillBioentityLinks({sequenceId, autoFillFeatureName: 'transcription', analyticsFn});
}

export async function bulkAutofillTranscriptions(
  sequenceIds: Array<string>,
  analyticsFn: (props: AutoFillFeatureResultProps) => void
): Promise<void> {
  await bulkAutoFillBioentityLinks({sequenceIds, autoFillFeatureName: 'transcription', analyticsFn});
}
